import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';

import { Box, Flex } from 'Components/Base';
import SvgIcon, { ICON_LIST } from 'Components/Base/Icon/SvgIcon';

export const MILESTONE_PERCENTAGE = {
  THIRTY: 30,
  EIGHTY: 80,
};

export const MILESTONES = [
  {
    pos: MILESTONE_PERCENTAGE.THIRTY,
    beforeIcon: ICON_LIST.FLAG,
    beforeIconColor: 'myPath.primary.500',
    beforeIconOffset: 2,
    afterIcon: ICON_LIST.CHECK_CIRCLE_OUTLINE,
    afterIconColor: 'myPath.primary.300',
    afterIconOffset: 0,
  },
  {
    pos: MILESTONE_PERCENTAGE.EIGHTY,
    beforeIcon: ICON_LIST.MEDAL_EVENTS,
    beforeIconColor: 'myPath.primary.500',
    beforeIconOffset: 0,
    afterIcon: ICON_LIST.CHECK_CIRCLE_OUTLINE,
    afterIconColor: 'myPath.primary.300',
    afterIconOffset: 0,
  },
];

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

function isWeekend(d) {
  const dow = d.day(); // 0=Sun .. 6=Sat
  return dow === 0 || dow === 6;
}
function max(a, b) {
  return a.isAfter(b) ? a : b;
}
function min(a, b) {
  return a.isBefore(b) ? a : b;
}

/**
 * Compute milestone timestamps by spreading each day's capacity uniformly
 * across the calendar day and prorating partial first/last days.
 *
 * Params:
 *  - start, end: "YYYY-MM-DD HH:mm:ss" or Dayjs
 *  - caps: { weekdayHours: number, weekendHours: number }
 *  - totalHours: total work hours (e.g. 100)
 *  - milestones: array of percents, e.g. [MILESTONE_PERCENTAGE.THIRTY, MILESTONE_PERCENTAGE.EIGHTY]
 *  - tz (optional): e.g. "Asia/Bangkok"
 *  - spreadOverActualDaySeconds (optional): default true; if false, uses 86,400s per day
 *
 * Returns:
 *  {
 *    totalAvailableHours: number,
 *    finishDate: Dayjs|null,
 *    milestones: [{ percent, targetHours, date: Dayjs|null }]
 *  }
 */
export function computeMilestoneDates(params) {
  const {
    caps: { weekdayHours, weekendHours },
    totalHours,
    milestones,
    tz,
    spreadOverActualDaySeconds = true,
  } = params;

  const parse = (v) => {
    if (dayjs.isDayjs(v)) return v.clone();
    const s = (v || '').includes('T') ? v : (v || '').replace(' ', 'T');
    return tz ? dayjs.tz(s, 'YYYY-MM-DDTHH:mm:ss', tz) : dayjs(s, 'YYYY-MM-DDTHH:mm:ss');
  };

  const start = parse(params.start);
  const end = parse(params.end);
  if (!start.isBefore(end)) throw new Error('start must be before end');
  if (weekdayHours < 0 || weekendHours < 0) throw new Error('caps must be non-negative');
  if (totalHours <= 0) {
    return {
      totalAvailableHours: 0,
      finishDate: null,
      milestones: [],
    };
  }

  // Prepare target hours
  const percents = milestones.slice();
  const targetsByPercent = new Map(percents.map((p) => [p, (p / 100) * totalHours]));
  const allTargets = Array.from(targetsByPercent.values()).sort((a, b) => a - b);
  const needed = new Set([...allTargets, totalHours]); // include 100%
  const achieved = new Map(); // targetHours -> Dayjs

  let cumulative = 0;

  // Tolerance in HOURS: ~3.6 microseconds (<< 1 second), just for float wiggle room.
  const EPS_HOURS = 1e-9;

  let cursor = start.startOf('day');
  while (cursor.isBefore(end)) {
    const dayStart = cursor;
    const dayEnd = cursor.add(1, 'day');

    const intervalStart = max(dayStart, start);
    const intervalEnd = min(dayEnd, end);
    const seconds = Math.max(0, intervalEnd.diff(intervalStart, 'second', true));

    if (seconds > 0) {
      const cap = isWeekend(dayStart) ? weekendHours : weekdayHours;
      const daySeconds = spreadOverActualDaySeconds
        ? Math.max(1, dayEnd.diff(dayStart, 'second', true))
        : 86400;
      const rate = cap / daySeconds; // hours per second
      const contribution = rate * seconds;

      // If no capacity this slice, skip milestone math (prevents 1/0 on tiny targets)
      if (contribution > 0 && rate > 0) {
        const pendingTargets = Array.from(needed)
          .filter((t) => !achieved.has(t))
          .sort((a, b) => a - b);

        for (const t of pendingTargets) {
          // Inclusive of the right edge with a tiny tolerance in HOURS
          if (cumulative < t && t <= cumulative + contribution + EPS_HOURS) {
            // seconds after intervalStart; clamp to [0, seconds] for safety
            const secNeeded = Math.min(seconds, Math.max(0, (t - cumulative) / rate));
            achieved.set(t, intervalStart.add(secNeeded, 'second'));
          }
        }
      }

      cumulative += contribution;
    }
    cursor = dayEnd;
  }

  const results = percents.map((percent) => {
    const target = targetsByPercent.get(percent);
    return { percent, targetHours: target, date: achieved.get(target) || dayjs(end) };
  });

  return results;
}

export const ENCOURAGEMENT_TYPE = {
  '30_ON_TRACK': '30_ON_TRACK', // Studied 1-30%, Date 1-30%
  '30_BEHIND': '30_BEHIND', // Studied 1-30%, Date > 30%
  '30_LEAD': '30_LEAD', // Studied > 31-80%, Date 1-30%
  '80_ON_TRACK': '80_ON_TRACK', // Studied > 31-80%, Date 31-80%
  '80_BEHIND': '80_BEHIND', // Studied > 31-80%, Date > 80%
  '80_LEAD': '80_LEAD', // Studied > 81%
  '100_COMPLETED': '100_COMPLETED', // Studied 100%

  NO_MILESTONE: 'NO_MILESTONE', // Exception case: no milestones
};

export const mapPercentCompleteToEncouragementType = (props) => {
  const { milestones, percentComplete, currentDate = dayjs() } = props;
  if (!milestones.length) {
    return ENCOURAGEMENT_TYPE['NO_MILESTONE'];
  }

  const thirthyPercentMilestone = milestones.find((m) => m.percent === MILESTONE_PERCENTAGE.THIRTY);
  const eightyPercentMilestone = milestones.find((m) => m.percent === MILESTONE_PERCENTAGE.EIGHTY);

  if (
    thirthyPercentMilestone.percent !== MILESTONE_PERCENTAGE.THIRTY &&
    thirthyPercentMilestone.percent !== MILESTONE_PERCENTAGE.EIGHTY
  ) {
    throw new Error('Milestone percent support only 30 and 80');
  }

  if (
    percentComplete < thirthyPercentMilestone.percent &&
    currentDate <= thirthyPercentMilestone.date
  ) {
    return ENCOURAGEMENT_TYPE['30_ON_TRACK'];
  } else if (
    percentComplete < thirthyPercentMilestone.percent &&
    currentDate > thirthyPercentMilestone.date
  ) {
    return ENCOURAGEMENT_TYPE['30_BEHIND'];
  } else if (
    percentComplete >= thirthyPercentMilestone.percent &&
    percentComplete < eightyPercentMilestone.percent &&
    currentDate <= thirthyPercentMilestone.date
  ) {
    return ENCOURAGEMENT_TYPE['30_LEAD'];
  } else if (
    percentComplete >= thirthyPercentMilestone.percent &&
    percentComplete < eightyPercentMilestone.percent &&
    currentDate > thirthyPercentMilestone.date &&
    currentDate <= eightyPercentMilestone.date
  ) {
    return ENCOURAGEMENT_TYPE['80_ON_TRACK'];
  } else if (
    percentComplete >= thirthyPercentMilestone.percent &&
    percentComplete < eightyPercentMilestone.percent &&
    currentDate > eightyPercentMilestone.date
  ) {
    return ENCOURAGEMENT_TYPE['80_BEHIND'];
  } else if (percentComplete >= eightyPercentMilestone.percent && percentComplete < 100) {
    return ENCOURAGEMENT_TYPE['80_LEAD'];
  } else if (percentComplete >= 100) {
    return ENCOURAGEMENT_TYPE['100_COMPLETED'];
  } else {
    return ENCOURAGEMENT_TYPE['NO_MILESTONE'];
  }
};
// Styled
const TextRow = styled(Flex)`
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  ${({ $stackOnMobile, theme }) =>
    $stackOnMobile &&
    `
    @media (max-width: ${theme?.breakpoints?.[0] ?? '512px'}) {
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    }
  `}
`;

const Inline = styled(Flex)`
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
`;

const Icon = styled(SvgIcon)`
  flex-shrink: 0;
`;

const SpanPrimary = styled(Box).attrs({ component: 'span' })`
  color: ${({ theme }) => theme.colors.myPath.primary[500]};
`;

const SpanSuccess = styled(Box).attrs({ component: 'span' })`
  color: ${({ theme }) => theme.colors.myPath.success[500]};
`;

const SpanError = styled(Box).attrs({ component: 'span' })`
  color: ${({ theme }) => theme.colors.myPath.error[500]};
`;

// Logic
export function mapCompletedMilestonesToEncouragementText(encouragementType, milestones) {
  if (!milestones?.length) return null;

  const thirtyPercentMilestone = milestones.find((m) => m.percent === MILESTONE_PERCENTAGE.THIRTY);
  const eightyPercentMilestone = milestones.find((m) => m.percent === MILESTONE_PERCENTAGE.EIGHTY);

  if (!thirtyPercentMilestone || !eightyPercentMilestone) {
    throw new Error(
      `Milestone percent support only 30 and 80 but got ${milestones
        .map((m) => m.percent)
        .join(', ')}`
    );
  }

  switch (encouragementType) {
    case ENCOURAGEMENT_TYPE['30_ON_TRACK']:
      return (
        <TextRow>
          ควรเรียนให้ถึงภายในวันที่
          <Icon aria-hidden icon={ICON_LIST.FLAG} size="16" color="myPath.primary.500" />
          <SpanPrimary>{thirtyPercentMilestone.date.format('DD MMM BBBB')}</SpanPrimary>
        </TextRow>
      );

    case ENCOURAGEMENT_TYPE['30_BEHIND']:
      return (
        <TextRow>
          <Icon aria-hidden icon={ICON_LIST.FIRE} size="16" color="myPath.error.500" />
          <SpanError>พยายามอีกหน่อย ใกล้จะถึง</SpanError>
          <Icon aria-hidden icon={ICON_LIST.MEDAL_EVENTS} size="16" color="myPath.error.500" />
          <SpanError>แล้ว</SpanError>
        </TextRow>
      );

    case ENCOURAGEMENT_TYPE['30_LEAD']:
      return (
        <TextRow $stackOnMobile>
          <Inline>
            <Icon
              aria-hidden
              icon={ICON_LIST.CHECK_CIRCLE_OUTLINE}
              size="16"
              color="myPath.success.500"
            />
            <SpanSuccess>เก่งมาก! เรียนเกิน</SpanSuccess>
            <Icon aria-hidden icon={ICON_LIST.FLAG} size="16" color="myPath.success.500" />
            <SpanSuccess>แล้ว!</SpanSuccess>
          </Inline>
          <Inline>
            ลุยต่อ เรียนให้จบ
            <Icon aria-hidden icon={ICON_LIST.MEDAL_EVENTS} size="16" color="myPath.primary.500" />
            ภายในวันที่
            <SpanPrimary>{eightyPercentMilestone.date.format('DD MMM BBBB')}</SpanPrimary>
          </Inline>
        </TextRow>
      );

    case ENCOURAGEMENT_TYPE['80_ON_TRACK']:
      return (
        <TextRow>
          ลุยต่อ เรียนให้จบ
          <Icon aria-hidden icon={ICON_LIST.MEDAL_EVENTS} size="16" color="myPath.primary.500" />
          ภายในวันที่ <SpanPrimary>{eightyPercentMilestone.date.format('DD MMM BBBB')}</SpanPrimary>
        </TextRow>
      );

    case ENCOURAGEMENT_TYPE['80_BEHIND']:
      return (
        <TextRow>
          <Icon aria-hidden icon={ICON_LIST.FIRE} size="16" color="myPath.error.500" />
          <SpanError>พยายามอีกหน่อย ใกล้จะถึง</SpanError>
          <Icon aria-hidden icon={ICON_LIST.MEDAL_EVENTS} size="16" color="myPath.error.500" />
          <SpanError>แล้ว</SpanError>
        </TextRow>
      );

    case ENCOURAGEMENT_TYPE['80_LEAD']:
      return (
        <TextRow $stackOnMobile>
          <Inline>
            <Icon
              aria-hidden
              icon={ICON_LIST.CHECK_CIRCLE_OUTLINE}
              size="16"
              color="myPath.success.500"
            />
            <SpanSuccess>เก่งมาก! เรียนเกิน</SpanSuccess>
            <Icon aria-hidden icon={ICON_LIST.MEDAL_EVENTS} size="16" color="myPath.success.500" />
            <SpanSuccess>แล้ว!</SpanSuccess>
          </Inline>
          <Inline>อย่าลืมทบทวนด้วยนะ</Inline>
        </TextRow>
      );

    case ENCOURAGEMENT_TYPE['100_COMPLETED']:
      return (
        <TextRow>
          <Icon
            aria-hidden
            icon={ICON_LIST.CHECK_CIRCLE_OUTLINE}
            size="16"
            color="myPath.success.500"
          />
          <SpanSuccess>เก่งมาก! เรียนจบแล้ว</SpanSuccess>
        </TextRow>
      );

    default:
      return null;
  }
}
